import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Button, Checkbox, PageShell, RadioGroup, SectionCard, FormSection, Input, Switch, ImageUploadField,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { useImageUploadQueue } from '@/lib/upload';
import { friendlyMessage, friendlyMessageWithDetails } from '@/lib/errors';
import { ProductSelector } from '@/features/catalog/components/ProductSelector';
import { useHomeBannerQuery, useHomeBannersQuery, useHomeBannerMutation } from '../hooks/useHomeBannersQuery';
import { instantToLocalInput, localInputToInstant } from '../lib/datetimeLocal';
import {
  BANNER_LOCALES,
  DEFAULT_VISIBLE_BY_LOCALE,
  EMPTY_HREF_BY_LOCALE,
  localeLabels,
  normalizeHrefByLocale,
  normalizeVisibleByLocale,
  setHref,
  setVisible,
} from '../lib/bannerLocale';
import type { BannerLocale, HomeBannerRequest } from '../types/cms';

// ID45: 폼 상태는 datetime-local 표시값(로컬 'YYYY-MM-DDTHH:mm')을 별도로 보관.
// 전송 직전 localInputToInstant 로 Instant 변환, 로드 시 instantToLocalInput 으로 복원.
interface BannerScheduleForm {
  displayStartLocal: string;
  displayEndLocal: string;
}

// textColor 기본 'dark' — 고도몰 이관 배너가 전부 dark 이고(고도몰 제목은 스킨에서 진한 색 고정), 히어로 배경이 밝은
// 블러라 light 기본이면 새 배너의 글자가 흐리게 나온다(QA-293).
const EMPTY: HomeBannerRequest = {
  bannerKey: '', albumTitle: '', artistName: '', eyebrow: '', tagline: '',
  accentHex: '#000000', textColor: 'dark', href: '', ctaLabel: '', imageUrl: '',
  isActive: true, sortOrder: 0, productId: null, taglineColor: null,
  hrefByLocale: { ...EMPTY_HREF_BY_LOCALE },
  visibleByLocale: { ...DEFAULT_VISIBLE_BY_LOCALE },
};

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;
/** 직접 지정을 처음 켤 때의 시작값 — 밝은 배경에서도 읽히는 중간 회색. */
const DEFAULT_TAGLINE_COLOR = '#454C53';

// C-1: 배너 키는 운영자에게 의미 없는 내부 식별자 → UI 미노출, 생성 시 자동 발급.
const slugify = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48);

const shortUid = (): string => crypto.randomUUID().replace(/-/g, '').slice(0, 12);

const generateBannerKey = (albumTitle: string, artistName: string): string => {
  const base = slugify(`${artistName} ${albumTitle}`);
  return base ? `${base}-${shortUid()}`.slice(0, 64) : `banner-${shortUid()}`;
};

// QA-56: required 는 저장 검증(handleSubmit)이 하드 강제하는 필드만 표기.
// BE 필수와 같게 표기·선검증한다 — 어긋나면 BE 가 400 으로 거부하고 BO 는 무엇이 문제인지 보여 주지 못한다.
// catalog release-qa/v1.0.23(PR #36)부터 아티스트·아이브로우·태그라인·CTA 는 선택값이다(고도몰 이관 배너는 이 값이
// 없다 — 쇼핑몰은 CTA 가 비면 "자세히보기"). 필수는 앨범명·배너 이미지뿐.
const TEXT_FIELDS: { key: keyof HomeBannerRequest; label: string; placeholder?: string; required?: boolean }[] = [
  { key: 'albumTitle', label: '앨범명', placeholder: '예) 정규 1집 [Title]', required: true },
  { key: 'artistName', label: '아티스트' },
  { key: 'eyebrow', label: '아이브로우', placeholder: '상단 강조 문구 (예: NEW RELEASE)' },
  { key: 'tagline', label: '태그라인', placeholder: '한 줄 소개' },
  // CTA 버튼 텍스트는 입력란을 숨긴다 — 쇼핑몰 히어로는 2026-05-27(storefront 163f2b2) 재작성 이후 배너별 CTA 를
  // 쓰지 않고 언어별 공통 문구("자세히보기")를 쓴다(고도몰도 "MORE" 고정). 값은 폼 상태에 그대로 두어 저장 시 보존한다.
];

const HEX_6 = /^#[0-9A-Fa-f]{6}$/;

/** BE 검증 실패 error.details 의 칸 키 → 화면 라벨(숨긴 칸도 BE 가 지목할 수 있어 포함). */
const FIELD_LABELS: Readonly<Record<string, string>> = {
  ...Object.fromEntries(TEXT_FIELDS.map((f) => [f.key, f.label])),
  ctaLabel: 'CTA 버튼 텍스트',
  imageUrl: '배너 이미지',
  accentHex: '강조 색상',
  textColor: '텍스트 색상',
  taglineColor: '서브 텍스트 색상',
  href: '직접 링크',
  productId: '연결 상품',
  bannerKey: '배너 키',
  displayStartAt: '노출 시작 일시',
  displayEndAt: '노출 종료 일시',
};
/**
 * 강조 색상은 입력란을 숨긴다 — 쇼핑몰 히어로는 2026-05-27(storefront 163f2b2) 재작성 이후 accentHex 를 쓰지 않는다
 * (그 전엔 슬라이드 배경색). 고도몰 color 도 스킨 CSS 에 덮여 효과가 없었다. BE 는 여전히 #RRGGBB 필수라 저장된 값을
 * 보존해 보내고, 형식이 깨진 값이면 운영자가 고칠 수 없으므로 막지 않고 기본값으로 보낸다.
 */
const DEFAULT_ACCENT_HEX = '#000000';

export const HomeBannerEditPage = () => {
  const { bannerId } = useParams<{ bannerId: string }>();
  const navigate = useNavigate();
  // '/cms/home-banners/new' 정적 라우트 → 파라미터 없음. 없거나 'new' 이면 등록.
  const isNew = !bannerId || bannerId === 'new';
  const id = isNew ? 0 : Number(bannerId);

  const query = useHomeBannerQuery(id);
  // QA-293: 서브 텍스트 색상은 BE(catalog) 가 taglineColor 를 받아야 저장된다. 응답에 키가 있으면
  // 반영된 것으로 보고 입력을 연다 — 반영 전에 열면 저장이 조용히 버려진다. 등록 화면은 목록 응답으로 판단.
  const listQuery = useHomeBannersQuery(0, 20);
  const supportsTaglineColor = isNew
    ? (listQuery.data?.content ?? []).some((b) => 'taglineColor' in b)
    : query.data != null && 'taglineColor' in query.data;
  const { create, update, remove } = useHomeBannerMutation();

  const [form, setForm] = useState<HomeBannerRequest>(EMPTY);
  const [schedule, setSchedule] = useState<BannerScheduleForm>({ displayStartLocal: '', displayEndLocal: '' });
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const uploads = useImageUploadQueue();

  useEffect(() => {
    if (query.data) {
      setForm({
        bannerKey: query.data.bannerKey,
        albumTitle: query.data.albumTitle,
        artistName: query.data.artistName,
        eyebrow: query.data.eyebrow,
        tagline: query.data.tagline,
        accentHex: query.data.accentHex,
        textColor: query.data.textColor,
        href: query.data.href,
        ctaLabel: query.data.ctaLabel,
        imageUrl: query.data.imageUrl,
        isActive: query.data.isActive,
        sortOrder: query.data.sortOrder,
        productId: query.data.productId ?? null,
        taglineColor: query.data.taglineColor ?? null,
        // ID45: 국가별 링크/노출 복원. 응답은 항상 4키지만 방어적으로 기본값과 병합.
        hrefByLocale: normalizeHrefByLocale(query.data.hrefByLocale),
        visibleByLocale: normalizeVisibleByLocale(query.data.visibleByLocale),
      });
      setSchedule({
        displayStartLocal: instantToLocalInput(query.data.displayStartAt),
        displayEndLocal: instantToLocalInput(query.data.displayEndAt),
      });
    }
  }, [query.data]);

  const set = <K extends keyof HomeBannerRequest>(key: K, val: HomeBannerRequest[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  // ID45: 국가별 링크 1건 갱신(빈 문자열은 null=폴백으로 정규화).
  const setHrefForLocale = (locale: BannerLocale, value: string) =>
    setForm((prev) => ({
      ...prev,
      hrefByLocale: setHref(prev.hrefByLocale ?? EMPTY_HREF_BY_LOCALE, locale, value),
    }));

  // ID45: 국가별 노출 토글 1건 갱신.
  const setVisibleForLocale = (locale: BannerLocale, value: boolean) =>
    setForm((prev) => ({
      ...prev,
      visibleByLocale: setVisible(prev.visibleByLocale ?? DEFAULT_VISIBLE_BY_LOCALE, locale, value),
    }));

  const handleSubmit = async () => {
    setError(null);
    const missing = TEXT_FIELDS.filter((f) => f.required && !String(form[f.key] ?? '').trim()).map((f) => f.label);
    if (missing.length > 0) {
      setError(`필수 항목을 입력해 주세요: ${missing.join(', ')}`);
      return;
    }
    // C-3: 연결 상품과 직접 링크 둘 다 비어 있으면 클릭 대상이 없음.
    if (!form.productId && !form.href.trim()) {
      setError('연결 상품을 선택하거나 직접 링크를 입력해 주세요.');
      return;
    }
    // ID45: 노출 예약 윈도우 변환 + 경계 검증(종료 < 시작 → 거부). BE 도 400 으로 막지만 선검증.
    const displayStartAt = localInputToInstant(schedule.displayStartLocal);
    const displayEndAt = localInputToInstant(schedule.displayEndLocal);
    if (displayStartAt && displayEndAt && new Date(displayEndAt) < new Date(displayStartAt)) {
      setError('노출 종료 일시는 시작 일시보다 빠를 수 없습니다.');
      return;
    }
    const taglineColor = supportsTaglineColor ? form.taglineColor?.trim() || null : null;
    if (taglineColor && !HEX_COLOR.test(taglineColor)) {
      setError('서브 텍스트 색상은 #RRGGBB 형식으로 입력해 주세요.');
      return;
    }
    try {
      const resolved = await uploads.flush();
      const imageUrl = typeof resolved.imageUrl === 'string' ? resolved.imageUrl : '';
      if (!imageUrl) {
        setError('배너 이미지를 등록해 주세요.');
        return;
      }
      // 명시적 null 전송(omit 아님): "예약/노출 종료 비움(상시노출)" 의도가 BE 로 전달돼야 한다.
      const { taglineColor: _formTaglineColor, ...rest } = form;
      const body: HomeBannerRequest = {
        ...rest,
        accentHex: HEX_6.test(form.accentHex.trim()) ? form.accentHex.trim() : DEFAULT_ACCENT_HEX,
        // 미지원 BE 에는 키를 보내지 않는다. 지원 시 null 은 "기본색으로 되돌림"이라 명시 전송.
        ...(supportsTaglineColor ? { taglineColor } : {}),
        imageUrl,
        displayStartAt,
        displayEndAt,
        hrefByLocale: form.hrefByLocale ?? { ...EMPTY_HREF_BY_LOCALE },
        visibleByLocale: form.visibleByLocale ?? { ...DEFAULT_VISIBLE_BY_LOCALE },
      };
      if (isNew) {
        await create.mutateAsync({
          ...body,
          bannerKey: generateBannerKey(form.albumTitle, form.artistName),
        });
      } else {
        await update.mutateAsync({ id, body });
      }
      navigate('/cms/home-banners');
    } catch (e) {
      // BE 사유(검증 메시지 등)를 보여 준다 — 종전엔 "update failed: 400" 만 떠서 무엇이 문제인지 알 수 없었다.
      // BE 검증 실패는 칸별 사유(error.details)까지 — 예 "요청 검증 실패 — 앨범명: 공백일 수 없습니다".
      setError(friendlyMessageWithDetails(e, '저장에 실패했습니다.', FIELD_LABELS));
    }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(id);
      navigate('/cms/home-banners');
    } catch (e) {
      setError(friendlyMessage(e, '삭제에 실패했습니다.'));
    }
  };

  const isBusy = create.isPending || update.isPending || remove.isPending;
  // 수정 모드에서 기존 데이터 로드 전에는 저장을 막는다(하이드레이션 레이스). 막지 않으면
  // 폼이 EMPTY 기본값일 때 저장돼 stale 값이 영속된다. isLoading(v5: enabled+fetching)은
  // new 모드(enabled=false/idle)에선 false 라 안전하다. (isError 는 아래 별도 처리.)
  const isLoadingExisting = !isNew && query.isLoading;

  const breadcrumb = [
    { label: 'CMS', href: '/cms/home-banners' },
    { label: 'Hero 배너 관리', href: '/cms/home-banners' },
    { label: isNew ? '등록' : '수정' },
  ];

  // 편집 모드에서 단건 조회가 실패하면(예: GET-by-id 미지원 405) 폼은 빈 값으로 로드되어
  // 마치 신규 등록처럼 보이고, 저장 시 엉뚱한 검증 에러만 나거나 변경이 묻힌다. 빈 폼을 띄우는
  // 대신 로드 실패를 명시하고 재시도/목록 이동을 제공한다(목록 패널의 isError 처리와 동일 UX).
  if (!isNew && query.isError) {
    return (
      <PageShell title="배너 수정" breadcrumb={breadcrumb} data-testid="home-banner-edit-page">
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-3 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>배너 정보를 불러오지 못했습니다. 다시 시도해 주세요.</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              다시 시도
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/cms/home-banners')}>
              목록으로
            </Button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={isNew ? '배너 등록' : '배너 수정'}
      breadcrumb={breadcrumb}
      data-testid="home-banner-edit-page"
      actions={
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={isBusy}>
              삭제
            </Button>
          )}
          <Button size="sm" onClick={handleSubmit} disabled={isBusy || uploads.isUploading || isLoadingExisting}>
            {uploads.isUploading
              ? '이미지 업로드 중…'
              : isLoadingExisting
                ? '불러오는 중…'
                : isNew
                  ? '등록'
                  : '저장'}
          </Button>
        </div>
      }
    >
      {(error || uploads.error) && (
        <div className="rounded-md p-3 text-sm" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
          {error ?? uploads.error}
        </div>
      )}

      <SectionCard title="기본 정보">
        <div className="flex flex-col gap-3">
          {TEXT_FIELDS.map(({ key, label, placeholder, required }) => (
            <FormSection key={key} label={label} density="compact" required={required ?? false}>
              <Input
                type="text"
                value={String(form[key] ?? '')}
                onChange={(e) => set(key, e.target.value as HomeBannerRequest[typeof key])}
                {...(placeholder ? { placeholder } : {})}
              />
            </FormSection>
          ))}
          <FormSection label="배너 이미지" density="compact" required>
            <ImageUploadField
              queue={uploads}
              name="imageUrl"
              category="CMS_BANNER"
              value={form.imageUrl || null}
              onChange={(url) => set('imageUrl', url ?? '')}
              disabled={isBusy}
            />
          </FormSection>
          <FormSection
            label="서브 텍스트 색상"
            density="compact"
            hint={
              supportsTaglineColor
                ? '태그라인(제목 아래 한 줄) 색상입니다. 끄면 쇼핑몰 기본색을 씁니다.'
                : '서버 반영 후 사용할 수 있습니다. 반영되면 이 화면에서 바로 켜집니다.'
            }
          >
            <div className="flex items-center gap-3" data-testid="tagline-color-field">
              <label
                className="inline-flex flex-shrink-0 items-center gap-2 text-sm"
                style={{ color: 'var(--content-default)' }}
              >
                <Checkbox
                  checked={form.taglineColor != null}
                  onCheckedChange={(v) => set('taglineColor', v ? DEFAULT_TAGLINE_COLOR : null)}
                  disabled={!supportsTaglineColor || isBusy}
                  aria-label="서브 텍스트 색상 직접 지정"
                  data-testid="tagline-color-custom"
                />
                직접 지정
              </label>
              {form.taglineColor != null && supportsTaglineColor ? (
                <>
                  <input
                    type="color"
                    value={HEX_COLOR.test(form.taglineColor) ? form.taglineColor : DEFAULT_TAGLINE_COLOR}
                    onChange={(e) => set('taglineColor', e.target.value)}
                    aria-label="서브 텍스트 색상"
                    className="h-9 w-12 cursor-pointer rounded border"
                    style={{ borderColor: 'var(--divider-strong)', background: 'var(--surface-0)' }}
                  />
                  <Input
                    type="text"
                    value={form.taglineColor}
                    onChange={(e) => set('taglineColor', e.target.value)}
                    placeholder={DEFAULT_TAGLINE_COLOR}
                    className="max-w-[140px] font-mono"
                    aria-label="서브 텍스트 색상 코드"
                    data-testid="tagline-color-hex"
                  />
                  {form.tagline ? (
                    <span
                      className="truncate text-sm font-medium"
                      style={{ color: HEX_COLOR.test(form.taglineColor) ? form.taglineColor : undefined }}
                      data-testid="tagline-color-sample"
                    >
                      {form.tagline}
                    </span>
                  ) : null}
                </>
              ) : null}
            </div>
          </FormSection>
        </div>
      </SectionCard>

      <SectionCard title="연결 대상" description="배너 클릭 시 이동할 대상입니다. 상품을 연결하면 링크가 자동 생성됩니다.">
        <div className="flex flex-col gap-3">
          <FormSection label="연결 상품" density="compact">
            <ProductSelector
              value={form.productId}
              onChange={(productId) => set('productId', productId)}
              placeholder="상품명으로 검색"
            />
          </FormSection>
          <FormSection
            label="직접 링크 (선택)"
            density="compact"
            hint="상품을 연결하지 않을 때만 사용합니다. 외부/특수 페이지로 이동할 경로."
          >
            <Input
              type="text"
              value={form.href}
              onChange={(e) => set('href', e.target.value)}
              placeholder="/events/..."
              disabled={!!form.productId}
            />
          </FormSection>
        </div>
      </SectionCard>

      {/* ID45: 노출 예약 — 시작/종료 비우면 상시 노출. */}
      <SectionCard
        title="노출 예약"
        description="설정한 기간에만 배너를 노출합니다. 둘 다 비우면 상시 노출됩니다."
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormSection label="노출 시작 일시" density="compact" hint="비우면 즉시 노출">
            <Input
              type="datetime-local"
              value={schedule.displayStartLocal}
              onChange={(e) => setSchedule((prev) => ({ ...prev, displayStartLocal: e.target.value }))}
              aria-label="노출 시작 일시"
            />
          </FormSection>
          <FormSection label="노출 종료 일시" density="compact" hint="비우면 상시 노출 (수동 종료 전까지)">
            <Input
              type="datetime-local"
              value={schedule.displayEndLocal}
              onChange={(e) => setSchedule((prev) => ({ ...prev, displayEndLocal: e.target.value }))}
              aria-label="노출 종료 일시"
            />
          </FormSection>
        </div>
      </SectionCard>

      {/* ID45: 국가별 링크/노출 — 링크 미입력 시 위 직접 링크/상품 링크로 폴백. */}
      <SectionCard
        title="국가별 링크 / 노출"
        description="국가(언어)별로 클릭 목적지와 노출 여부를 개별 지정합니다. 링크를 비우면 기본 링크를 사용합니다."
      >
        <div className="flex flex-col gap-3">
          {BANNER_LOCALES.map((locale) => (
            <FormSection key={locale} label={localeLabels[locale]} density="compact">
              <div className="flex items-center gap-3">
                <Input
                  type="text"
                  value={form.hrefByLocale?.[locale] ?? ''}
                  onChange={(e) => setHrefForLocale(locale, e.target.value)}
                  placeholder="기본 링크 사용 (비워두면 폴백)"
                  className="flex-1"
                  aria-label={`${localeLabels[locale]} 링크`}
                />
                <label
                  className="inline-flex flex-shrink-0 items-center gap-2 text-sm"
                  style={{ color: 'var(--content-default)' }}
                >
                  <Switch
                    checked={form.visibleByLocale?.[locale] ?? true}
                    onCheckedChange={(v) => setVisibleForLocale(locale, v)}
                    aria-label={`${localeLabels[locale]} 노출`}
                  />
                  노출
                </label>
              </div>
            </FormSection>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="표시 설정">
        <div className="flex flex-col gap-3">
          <FormSection label="활성 여부" density="compact">
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => set('isActive', v)}
              aria-label="활성 여부"
            />
          </FormSection>
          <FormSection label="정렬 순서" density="compact">
            <Input
              type="number"
              value={form.sortOrder}
              onChange={(e) => set('sortOrder', Number(e.target.value))}
              className="max-w-[160px]"
            />
          </FormSection>
          <FormSection label="텍스트 색상" density="compact">
            <RadioGroup
              value={form.textColor}
              onValueChange={(v) => set('textColor', v)}
              orientation="horizontal"
              aria-label="텍스트 색상"
              options={[
                { value: 'light', label: '밝은 텍스트' },
                { value: 'dark', label: '어두운 텍스트' },
              ]}
            />
          </FormSection>
        </div>
      </SectionCard>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>배너 삭제</DialogTitle>
            <DialogDescription>
              "{form.albumTitle}" 배너를 삭제하시겠습니까?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">취소</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={isBusy}
              onClick={() => { setConfirmDelete(false); void handleDelete(); }}
            >
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
